import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId, isWidgetWrapping } from 'widgets/Widget.helpers';
import { responsiveShapeResize } from 'widgets/store';

import { OnResizeStartEvent } from '../BoundingBox.types';
import { ResponsiveShape } from './responsiveGroup.types';

/**
 * Returns an array of text widgets that are wrapped by the shape widget
 * If no text widgets are wrapped by the shape widget, then an empty array is returned
 *
 * @param textWidgets - Array of text widget elements
 * @param shapeSize - The size of the shape widget
 *
 * @returns Array of text widget elements wrapped by the shape widget
 */
const getTextWidgetsWrappedByShape = (
  textWidgets: HTMLElement[],
  shapeSize: { topPx: number; leftPx: number; widthPx: number; heightPx: number },
) => {
  const textWidgetsWrappedByShape: HTMLElement[] = [];

  // find all the text widgets that are contained by the shape widget in the group
  textWidgets.forEach((textWidgetEl: HTMLElement) => {
    const textWidgetTopPx = parseFloat(textWidgetEl.style.top);
    const textWidgetWidthPx = parseFloat(textWidgetEl.style.width);
    const textWidgetLeftPx = parseFloat(textWidgetEl.style.left);
    const textWidgetHeightPx = textWidgetEl.clientHeight;

    const textSize = {
      topPx: textWidgetTopPx,
      leftPx: textWidgetLeftPx,
      widthPx: textWidgetWidthPx,
      heightPx: textWidgetHeightPx,
    };

    const isWrapping = isWidgetWrapping(shapeSize, textSize);
    if (isWrapping) textWidgetsWrappedByShape.push(textWidgetEl);
  });

  return textWidgetsWrappedByShape;
};

/**
 * Check if a text widget is wrapped by another text widget
 * (in that case it won't have a gap below it that needs to be kept when resizing)
 *
 * @param textWidgets - Array of text widget elements
 * @param textWidgetEl - The text widget element to check
 * @param idx - The index of the text widget element in the textWidgets array
 */
const isTextWidgetWrappedByOtherTextWidget = (
  textWidgets: HTMLElement[],
  textWidgetEl: HTMLElement,
  idx: number,
): boolean => {
  // no bottom padding gap in this case
  let isOverlappingTextWidget = false;
  const textWidgetTopPx = parseFloat(textWidgetEl.style.top);

  // check if it's completely wrapped by another text widget - in that case we don't need it for the height calculation later
  for (let i = 0; i < textWidgets.length; i++) {
    if (i === idx) continue;

    const otherTextWidgetEl = textWidgets[i];
    const otherTextWidgetTop = parseFloat(otherTextWidgetEl.style.top);
    const otherTextWidgetBottom = otherTextWidgetTop + otherTextWidgetEl.clientHeight;

    // if it's completely wrapped by another text widget
    // doesn't have to be width wise, just height wise
    const isTopPxGreater = textWidgetTopPx > otherTextWidgetTop;
    const isBottomPxLess = textWidgetTopPx + textWidgetEl.clientHeight < otherTextWidgetBottom;
    const isCompletelyOverlapped = isTopPxGreater && isBottomPxLess;

    if (isCompletelyOverlapped) {
      isOverlappingTextWidget = true;
      break;
    }
  }

  return isOverlappingTextWidget;
};

/**
 * Finds the top and bottom padding of the shape widget
 * (padding is later kept while resizing the shape)
 *
 * The top padding is the distance from the top of the shape to the top of the first text widget
 * The bottom padding is the distance from the bottom of the shape to the bottom of the last text widget
 *
 * @param textWidgetsWrappedByShape - Array of text widget elements wrapped by the shape widget
 * @param shapeSize - The size of the shape widget
 *
 * @returns { topPadding: number, bottomPadding: number }
 */
const findTopAndBottomPadding = (
  textWidgetsWrappedByShape: HTMLElement[],
  shapeSize: { topPx: number; widthPx: number; heightPx: number },
) => {
  let topPx = parseFloat(textWidgetsWrappedByShape[0].style.top);
  let bottomPx = parseFloat(textWidgetsWrappedByShape[0].style.top) + textWidgetsWrappedByShape[0].clientHeight;

  // find the highest top position and lowest bottom position of the text widgets wrapped by the shape
  textWidgetsWrappedByShape.forEach((textWidgetEl: HTMLElement) => {
    const textWidgetTopPx = parseFloat(textWidgetEl.style.top);
    const textWidgetBottomPx = textWidgetTopPx + textWidgetEl.clientHeight;

    if (textWidgetTopPx < topPx) topPx = textWidgetTopPx;
    if (textWidgetBottomPx > bottomPx) bottomPx = textWidgetBottomPx;
  });

  // cannot be negative - just in case
  const topPadding = Math.max(topPx - shapeSize.topPx, 0);
  const bottomPadding = Math.max(shapeSize.topPx + shapeSize.heightPx - bottomPx, 0);

  return { topPadding, bottomPadding };
};

/**
 * Sort the list of widget elements by their top position - highest position to lowest position
 * Obs: sorts the array in place
 *
 * @param widgets - Array of widget elements
 *
 * @returns same array of widget elements sorted by their top position
 */
const sortByTopPxInPlace = (widgets: HTMLElement[]) => {
  return widgets.sort((a: HTMLElement, b: HTMLElement) => {
    const aTopPx = parseFloat(a.style.top);
    const bTopPx = parseFloat(b.style.top);

    return aTopPx - bTopPx;
  });
};

/**
 * Creates a ResponsiveShape object from the provided textWidgets and shapeWidgets arrays.
 * The ResponsiveShape object contains the shapeId, bottomPadding, topPadding, and textWidgetIds
 *
 * (The shape object is used for responsively resizing widgets in a group from the side handle)
 *
 * @param {HTMLElement[]} textWidgets - The array to populate with text widgets.
 * @param {HTMLElement[]} shapeWidgets - The array to populate with basic shape widgets.
 *
 */
export const createResponsiveShape = (shapeWidgets: HTMLElement[], textWidgets: HTMLElement[]) => {
  const shapeWidgetEl = shapeWidgets[0];
  const shapeId = shapeWidgetEl.id;
  const topPx = parseFloat(shapeWidgetEl.style.top);
  const leftPx = parseFloat(shapeWidgetEl.style.left);
  const heightPx = parseFloat(shapeWidgetEl.style.height);
  const widthPx = parseFloat(shapeWidgetEl.style.width);
  const shapeSize = { topPx, widthPx, heightPx, leftPx };

  const textWidgetsWrappedByShape: HTMLElement[] = getTextWidgetsWrappedByShape(textWidgets, shapeSize);

  // if the shape does not wrap any text widgets
  if (!textWidgetsWrappedByShape?.length) return;

  // sort the text widgets by their top position - highest position to lowest position
  const sortedTextWidgetsByTop = sortByTopPxInPlace(textWidgetsWrappedByShape);

  // padding from the top/bottom text widget to the top/bottom of the shape
  // (padding is later kept while resizing the shape)
  const { topPadding, bottomPadding } = findTopAndBottomPadding(sortedTextWidgetsByTop, shapeSize);

  const responsiveTextWidgets = sortedTextWidgetsByTop.map((textWidgetEl: HTMLElement, idx: number) => {
    const textWidgetId = textWidgetEl.id;
    const isSingleTextWidget = sortedTextWidgetsByTop.length === 1;
    const isLast = idx === sortedTextWidgetsByTop.length - 1;
    const nextWidget = sortedTextWidgetsByTop[idx + 1];

    // if the text widget is completely overlapped by another text widget
    const isOverlappingTextWidget = isTextWidgetWrappedByOtherTextWidget(sortedTextWidgetsByTop, textWidgetEl, idx);

    // in this case, there is no padding to the text widget below this text widget
    const hasNoPaddingBottom = isSingleTextWidget || isLast || isOverlappingTextWidget || !nextWidget;
    if (hasNoPaddingBottom) return { id: textWidgetId, paddingBottom: 0, isOverlappingTextWidget, el: textWidgetEl };

    // find the padding / gap to the text widget below this text widget
    const nextWidgetTop = parseFloat(nextWidget.style.top);
    const currentTextWidgetBottom = parseFloat(textWidgetEl.style.top) + textWidgetEl.clientHeight;
    // (cannot be negative)
    const paddingBottom = Math.max(nextWidgetTop - currentTextWidgetBottom, 0);

    return { id: textWidgetId, el: textWidgetEl, paddingBottom, isOverlappingTextWidget: false };
  });

  const responsiveShape = {
    shapeId,
    shapeEl: shapeWidgetEl,
    bottomPadding,
    topPadding,
    textWidgets: responsiveTextWidgets,
  };

  return responsiveShape;
};

/**
 * Resizes the shape and text widgets in the provided responsiveShape object.
 * The height of the shape is the height of all the text widgets its wrapping + the gaps and padding
 * The text widgets are shifted down if there is a gap between them and the text widget above - relative to the height of the text widget above
 *
 * @param {ResponsiveShape} responsiveShape - The responsive shape object containing the shapeId, bottomPadding, topPadding, and textWidgetIds
 */
export const responsiveResizeGroupMembers = (responsiveShape: ResponsiveShape) => {
  const shapeId = responsiveShape.shapeId;
  const bottomPadding = responsiveShape.bottomPadding;
  const topPadding = responsiveShape.topPadding;
  const shapeWidgetEl = responsiveShape.shapeEl;

  if (!shapeWidgetEl) return;

  // change the top position of the texts widget - depending on the widget above it
  for (let i = 0; i < responsiveShape.textWidgets.length; i++) {
    // the first text widget doesn't have a widget above it
    if (i === 0) continue;

    const widgetAbove = responsiveShape.textWidgets[i - 1];
    const widgetAbovePaddingBottom = widgetAbove.paddingBottom;
    if (widgetAbovePaddingBottom === 0) continue;

    const widgetAboveEl = widgetAbove.el;
    if (!widgetAboveEl) continue;

    const currentTextWidgetEl = responsiveShape.textWidgets[i].el;
    if (!currentTextWidgetEl) continue;

    const widgetAboveBottomPx = parseFloat(widgetAboveEl.style.top) + widgetAboveEl.clientHeight;
    const top = parseFloat(currentTextWidgetEl.style.top);

    // if the gap to the above widget is not the same
    // then change the top position of the text widget
    if (top - widgetAboveBottomPx !== widgetAbovePaddingBottom) {
      currentTextWidgetEl.style.top = `${widgetAboveBottomPx + widgetAbovePaddingBottom}px`;
    }
  }

  // Update the height of the shape to the height of all the text widgets its wrapping
  let allTextWidgetHeightPx = 0;
  for (let i = 0; i < responsiveShape.textWidgets.length; i++) {
    const textWidget = responsiveShape.textWidgets[i];

    // skip text widgets that are completely overlapped by other text widgets
    if (textWidget.isOverlappingTextWidget) continue;

    const textWidgetEl = textWidget.el;
    const textWidgetHeightPx = textWidgetEl.clientHeight;
    // the gap below to the next widget should be considered for the height
    const textWidgetBottomPadding = textWidget.paddingBottom;
    allTextWidgetHeightPx += textWidgetHeightPx + textWidgetBottomPadding;
  }

  // increase the height of the shape by the height of the textWidgetEl + initialPadding
  const newHeightPx = allTextWidgetHeightPx + bottomPadding + topPadding;
  const widthPx = parseFloat(shapeWidgetEl.style.width);
  shapeWidgetEl.style.height = `${newHeightPx}px`;
  responsiveShapeResize.resize(shapeId, widthPx, newHeightPx);
};

/**
 * Populates the provided textWidgets and shapeWidgets arrays with the text and shape widgets
 * of the target in the provided OnResizeStartEvent.
 *
 * @param {OnResizeStartEvent} ev - The resize start event containing the target widget.
 * @param {HTMLElement[]} textWidgets - The array to populate with text widgets.
 * @param {HTMLElement[]} shapeWidgets - The array to populate with basic shape widgets.
 *
 * @returns {void}
 */
export const createResponsiveWidgetArr = (
  ev: OnResizeStartEvent,
  textWidgets: HTMLElement[],
  shapeWidgets: HTMLElement[],
) => {
  const widgetId = ev.target.id;
  const widgetType = getWidgetTypeFromId(widgetId);

  const isTextWidget = widgetType === WidgetType.Text;
  const isBasicShapeWidget = widgetType === WidgetType.BasicShape;

  if (isTextWidget) textWidgets.push(ev.target as HTMLElement);
  if (isBasicShapeWidget) shapeWidgets.push(ev.target as HTMLElement);
};
